import { supabase } from "@/integrations/supabase/client";
import { UserSubscription, SubscriptionStatus, UserSubscriptionQueryResult, DurationType, FeeType, DiscountType } from "@/types/subscription";
import { formatISO } from "date-fns";

// Transform from Supabase format to our app format
const transformUserSubscription = (row: UserSubscriptionQueryResult): UserSubscription => ({
  id: row.id,
  userId: row.user_id,
  subscriptionPlanId: row.subscription_plan_id,
  status: row.status as SubscriptionStatus,
  subscriptionStart: row.subscription_start,
  subscriptionEnd: row.subscription_end,
  price: row.price,
  currencyCode: row.currency_code,
  paymentMethodId: row.payment_method_id,
  paymentReference: row.payment_reference,
  promotionId: row.promotion_id,
  totalDiscount: row.total_discount && row.total_discount > 0 ? row.total_discount : undefined,
  promoDiscount: row.promo_discount && row.promo_discount > 0 ? row.promo_discount : undefined,
  referralDiscount: row.referral_discount && row.referral_discount > 0 ? row.referral_discount : undefined,
  autoRenew: row.auto_renew,
  createdAt: row.created_at,
  updatedAt: row.updated_at,

  // Device and platform information
  deviceType: row.device_type,
  osPlatform: row.os_platform,
  appVersion: row.app_version,

  // User experience metrics
  conversionSource: row.conversion_source,
  acquisitionChannel: row.acquisition_channel,

  // Subscription history
  previousPlanId: row.previous_plan_id,
  upgradeDate: row.upgrade_date,
  downgradeDate: row.downgrade_date,

  // Subscription management
  cancellationDate: row.cancellation_date,
  cancellationReason: row.cancellation_reason,

  // Administrative
  adminNotes: row.admin_notes,
  isTrial: row.is_trial,
  trialEndDate: row.trial_end_date,
  refunded: row.refunded,
  refundDate: row.refund_date,
  refundAmount: row.refund_amount,

  // Include subscription plan data if available
  subscriptionPlan: row.subscription_plan
    ? {
        id: row.subscription_plan.id,
        name: row.subscription_plan.name,
        description: row.subscription_plan.description,
        duration: row.subscription_plan.duration,
        durationType: row.subscription_plan.duration_type as DurationType,
        productId: row.subscription_plan.product_id,
        isActive: row.subscription_plan.is_active,
        createdAt: row.subscription_plan.created_at,
        updatedAt: row.subscription_plan.updated_at,
        planDiscountPercentage: row.subscription_plan.plan_discount,
        priceConfigurations: row.subscription_plan.price_configurations,
      }
    : undefined,

  // Include payment method data if available
  paymentMethod: row.payment_method
    ? {
        id: row.payment_method.id,
        name: row.payment_method.name,
        description: row.payment_method.description,
        platformFee: row.payment_method.platform_fee,
        feeType: row.payment_method.fee_type as FeeType,
        supportedCurrencies: row.payment_method.supported_currencies,
        isDefault: row.payment_method.is_default,
        isActive: row.payment_method.is_active,
        createdAt: row.payment_method.created_at,
        updatedAt: row.payment_method.updated_at,
      }
    : undefined,

  // Include promotion data if available
  promotion: row.promotion
    ? {
        id: row.promotion.id,
        name: row.promotion.name,
        description: row.promotion.description,
        code: row.promotion.code,
        discountValue: row.promotion.discount_value,
        discountType: row.promotion.discount_type as DiscountType,
        startDate: row.promotion.start_date,
        endDate: row.promotion.end_date,
        maxUses: row.promotion.max_uses,
        currentUses: row.promotion.current_uses,
        minPurchaseAmount: row.promotion.min_purchase_amount,
        applicablePlans: row.promotion.applicable_plans,
        isActive: row.promotion.is_active,
        createdAt: row.promotion.created_at,
        updatedAt: row.promotion.updated_at,
      }
    : undefined,
});

// Fetch a user's current active subscription
export const fetchUserSubscription = async (
  userId: string
): Promise<UserSubscription | null> => {
  try {
    // Parse the userID to integer for database queries
    const profileId = parseInt(userId, 10);

    if (isNaN(profileId)) {
      throw new Error(`Invalid profile ID: ${userId}`);
    }

    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        )
      `
      )
      .eq("user_id", profileId)
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "No rows returned" which is expected if no subscription exists
      console.error("Error fetching user subscription:", error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return transformUserSubscription(data);
  } catch (error) {
    console.error("Error in fetchUserSubscription:", error);
    throw error;
  }
};

// Fetch user's most recent subscription regardless of status (for admin view)
export const fetchUserLatestSubscription = async (
  userId: string
): Promise<UserSubscription | null> => {
  try {
    // Parse the userID to integer for database queries
    const profileId = parseInt(userId, 10);

    if (isNaN(profileId)) {
      throw new Error(`Invalid profile ID: ${userId}`);
    }

    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        )
      `
      )
      .eq("user_id", profileId)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "No rows returned" which is expected if no subscription exists
      console.error("Error fetching user latest subscription:", error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return transformUserSubscription(data);
  } catch (error) {
    console.error("Error in fetchUserLatestSubscription:", error);
    throw error;
  }
};

// Fetch all active user subscriptions (optimized for the users list view)
export const fetchAllActiveSubscriptions = async (): Promise<
  Map<number, UserSubscription>
> => {
  try {
    console.log("Fetching all active subscriptions...");

    const nowIso = new Date().toISOString();

    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end,
        price, currency_code,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name
        )
      `
      )
      .eq("status", "active")
      .lte("subscription_start", nowIso)
      .or(`subscription_end.is.null,subscription_end.gt.${nowIso}`)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching all active subscriptions:", error);
      throw error;
    }

    console.log(
      `Raw data count from query: ${
        data?.length || 0
      } active subscriptions found`
    );

    if (data && data.length > 0) {
      console.log(`Sample subscription data:`, data[0]);
    }

    // Create a map of user_id -> subscription for quick lookups
    const subscriptionsMap = new Map<number, UserSubscription>();

    if (data) {
      (data as unknown as UserSubscriptionQueryResult[]).forEach((subscription) => {
        // For each user, keep only their most recent active subscription
        if (!subscriptionsMap.has(subscription.user_id)) {
          subscriptionsMap.set(
            subscription.user_id,
            transformUserSubscription(subscription)
          );
        }
      });
    }

    console.log(
      `Final map size after processing: ${subscriptionsMap.size} unique users with active subscriptions`
    );

    return subscriptionsMap;
  } catch (error) {
    console.error("Error in fetchAllActiveSubscriptions:", error);
    throw error;
  }
};

// Fetch each user's latest subscription (any status) for UI display
export const fetchLatestSubscriptionsMap = async (): Promise<
  Map<number, UserSubscription>
> => {
  try {
    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status,
        subscription_start, subscription_end,
        price, currency_code, payment_method_id, payment_reference, promotion_id,
        total_discount, promo_discount, referral_discount,
        auto_renew, created_at, updated_at,
        device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, product_id, is_active, created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        )
      `
      )
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching latest subscriptions map:", error);
      throw error;
    }

    const latestMap = new Map<number, UserSubscription>();
    ((data || []) as unknown as UserSubscriptionQueryResult[]).forEach((row) => {
      if (!latestMap.has(row.user_id)) {
        latestMap.set(row.user_id, transformUserSubscription(row));
      }
    });

    return latestMap;
  } catch (error) {
    console.error("Error in fetchLatestSubscriptionsMap:", error);
    throw error;
  }
};

// Fetch subscription count from a specific time period for trend calculation
export const fetchSubscriptionCountForDate = async (
  date: Date
): Promise<number> => {
  try {
    const targetDate = date.toISOString();
    console.log(`Fetching subscription count for date: ${targetDate}`);

    // This query gets active subscriptions that were active on the given date
    // A subscription is active on a date if:
    // 1. It started before or on that date AND
    // 2. Either it has no end date OR its end date is after that date
    const { data, error } = await supabase
      .from("user_subscriptions")
      .select("id", { count: "exact" })
      .lte("subscription_start", targetDate)
      .or(`subscription_end.is.null,subscription_end.gt.${targetDate}`);

    if (error) {
      console.error("Error fetching subscription count for date:", error);
      throw error;
    }

    console.log(
      `Found ${data?.length || 0} subscriptions active on ${targetDate}`
    );

    if (data && data.length > 0) {
      console.log(
        `First few subscription IDs:`,
        data.slice(0, 3).map((sub) => sub.id)
      );
    }

    return data?.length || 0;
  } catch (error) {
    console.error("Error in fetchSubscriptionCountForDate:", error);
    return 0; // Return 0 as fallback
  }
};

// Fetch all user subscriptions (for admin overview)
export const fetchAllUserSubscriptions = async (): Promise<UserSubscription[]> => {
  try {
    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        ),
        user:profiles!user_subscriptions_user_id_fkey(
          id, name, email
        )
      `
      )
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching all user subscriptions:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    return data.map((row) => ({
      ...transformUserSubscription(row),
      user: row.user ? {
        id: row.user.id,
        name: row.user.name,
        email: row.user.email,
      } : undefined,
    }));
  } catch (error) {
    console.error("Error in fetchAllUserSubscriptions:", error);
    throw error;
  }
};

// Fetch user subscriptions with filters and pagination (for admin list)
export const fetchUserSubscriptionsFiltered = async (
  params: {
    status?: SubscriptionStatus | "all";
    planId?: number | "all";
    page?: number;
    pageSize?: number;
    search?: string;
  }
): Promise<{ items: UserSubscription[]; total: number }> => {
  try {
    const { status = "all", planId = "all", page = 1, pageSize = 10, search } = params || {};
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize - 1;

    const nowIso = new Date().toISOString();

    let query = supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        ),
        user:profiles!user_subscriptions_user_id_fkey(
          id, name, email
        )
      `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false })
      .range(startIndex, endIndex);

    // Apply plan filter
    if (planId !== "all" && typeof planId === "number") {
      query = query.eq("subscription_plan_id", planId);
    }

    // Apply status/date-aware filters
    if (status === "active") {
      query = query
        .eq("status", "active")
        .lte("subscription_start", nowIso)
        .or(`subscription_end.is.null,subscription_end.gt.${nowIso}`);
    } else if (status === "expired") {
      query = query
        .lte("subscription_end", nowIso)
        .neq("status", "cancelled");
    } else if (status === "cancelled") {
      query = query.eq("status", "cancelled");
    } else if (status === "pending") {
      query = query.eq("status", "pending");
    }

    // Apply free-text search across user name/email only
    const trimmed = (search || "").trim();
    if (trimmed.length > 0) {
      const like = `%${trimmed}%`;
      query = query.or(
        `name.ilike.${like},email.ilike.${like}`,
        { foreignTable: "profiles" }
      );
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching filtered user subscriptions:", error);
      throw error;
    }

    const items = (data || []).map((row: UserSubscriptionQueryResult & { user?: { id: number; name: string; email: string } }) => ({
      ...transformUserSubscription(row),
      user: row.user
        ? {
            id: row.user.id,
            name: row.user.name,
            email: row.user.email,
          }
        : undefined,
    }));

    return { items, total: count || 0 };
  } catch (error) {
    console.error("Error in fetchUserSubscriptionsFiltered:", error);
    throw error;
  }
};

// Fetch user subscriptions overview/statistics
export const fetchUserSubscriptionsOverview = async () => {
  try {
    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        "status, price, currency_code, total_discount, promo_discount, referral_discount, subscription_start, subscription_end"
      );

    if (error) {
      console.error("Error fetching user subscriptions overview:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      return {
        total: 0,
        active: 0,
        cancelled: 0,
        expired: 0,
        totalRevenue: 0,
        totalDiscounts: 0,
        totalPromoDiscounts: 0,
        totalReferralDiscounts: 0,
        subscriptionsWithDiscounts: 0,
      };
    }

    type OverviewRow = {
      status: string;
      price?: number | null;
      currency_code?: string | null;
      total_discount?: number | null;
      promo_discount?: number | null;
      referral_discount?: number | null;
      subscription_start?: string | null;
      subscription_end?: string | null;
    };

    const rows = (data || []) as unknown as OverviewRow[];

    const total = rows.length;
    const nowIso = new Date().toISOString();

    // A subscription is currently active if:
    // - status is "active"
    // - subscription_start <= now
    // - subscription_end is null OR subscription_end > now
    const isCurrentlyActive = (sub: OverviewRow) =>
      sub.status === "active" &&
      sub.subscription_start &&
      sub.subscription_start <= nowIso &&
      (!sub.subscription_end || sub.subscription_end > nowIso);

    // A subscription is counted as expired if:
    // - subscription_end exists AND subscription_end <= now AND status is not cancelled
    const isExpired = (sub: OverviewRow) =>
      !!sub.subscription_end && sub.subscription_end <= nowIso && sub.status !== "cancelled";

    const active = rows.filter(isCurrentlyActive).length;
    const cancelled = rows.filter((sub) => sub.status === "cancelled").length;
    const expired = rows.filter(isExpired).length;
    
    // Calculate total revenue excluding cancelled, grouped by currency
    const nonCancelled = rows.filter((sub) => sub.status !== "cancelled");
    const revenueByCurrency = nonCancelled.reduce<Record<string, number>>(
      (acc, sub) => {
        const code = (sub.currency_code || "INR").toUpperCase();
        const price = sub.price || 0;
        acc[code] = (acc[code] || 0) + price;
        return acc;
      },
      {}
    );
    const totalRevenueINR = revenueByCurrency.INR || 0;
    const totalRevenueUSD = revenueByCurrency.USD || 0;

    // Calculate discount statistics
    const totalDiscounts = rows.reduce((sum, sub) => sum + (sub.total_discount || 0), 0);
    const totalPromoDiscounts = rows.reduce((sum, sub) => sum + (sub.promo_discount || 0), 0);
    const totalReferralDiscounts = rows.reduce((sum, sub) => sum + (sub.referral_discount || 0), 0);
    const subscriptionsWithDiscounts = rows.filter(sub => (sub.total_discount || 0) > 0).length;

    return {
      total,
      active,
      cancelled,
      expired,
      totalRevenueINR,
      totalRevenueUSD,
      totalDiscounts,
      totalPromoDiscounts,
      totalReferralDiscounts,
      subscriptionsWithDiscounts,
    };
  } catch (error) {
    console.error("Error in fetchUserSubscriptionsOverview:", error);
    throw error;
  }
};

// Fetch a user's subscription history
export const fetchUserSubscriptionHistory = async (
  userId: string
): Promise<UserSubscription[]> => {
  try {
    // Parse the userID to integer for database queries
    const profileId = parseInt(userId, 10);

    if (isNaN(profileId)) {
      throw new Error(`Invalid profile ID: ${userId}`);
    }

    const { data, error } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        )
      `
      )
      .eq("user_id", profileId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user subscription history:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    return data.map(transformUserSubscription);
  } catch (error) {
    console.error("Error in fetchUserSubscriptionHistory:", error);
    throw error;
  }
};

// Create a new user subscription (for admin use)
export const createUserSubscription = async (subscription: {
  [x: string]: string | number | boolean | null | undefined;
  userId: string;
  subscriptionPlanId: number;
  status: string;
  subscriptionStart: string;
  subscriptionEnd?: string | null;
  price: number;
  currencyCode: string;
  paymentMethodId?: number | null;
  paymentReference?: string | null;
  promotionId?: number | null;
  promoDiscount?: number | null;
  referralDiscount?: number | null;
  referrerId?: number | null;
  totalDiscount?: number | null;
  autoRenew?: boolean;
  deviceType?: string | null;
  osPlatform?: string | null;
  appVersion?: string | null;
  conversionSource?: string | null;
  acquisitionChannel?: string | null;
  previousPlanId?: number | null;
  adminNotes?: string | null;
  upgradeDate?: string | null;
  downgradeDate?: string | null;
  cancellationDate?: string | null;
  cancellationReason?: string | null;
  isTrial?: boolean;
  trialEndDate?: string | null;
  refunded?: boolean;
  refundDate?: string | null;
  refundAmount?: number | null;
}): Promise<UserSubscription> => {
  try {
    // Cancel any active subscriptions first
    await cancelActiveSubscriptions(subscription.userId);

    // Parse the userID to integer for database storage
    const profileId = parseInt(subscription.userId, 10);

    if (isNaN(profileId)) {
      throw new Error(`Invalid profile ID: ${subscription.userId}`);
    }

    // Create insert data with explicit column names - aligned with our new table schema
    const insertData = {
      // Essential fields - user_id now an INTEGER referencing profiles.id
      user_id: profileId,
      subscription_plan_id: subscription.subscriptionPlanId,
      status: subscription.status,

      // Subscription timing
      subscription_start: subscription.subscriptionStart,
      subscription_end: subscription.subscriptionEnd || null,

      // Payment details
      price: subscription.price,
      currency_code: subscription.currencyCode,
      payment_method_id: subscription.paymentMethodId || null,
      payment_reference: subscription.paymentReference || null,
      promotion_id: subscription.promotionId || null,
      total_discount: subscription.totalDiscount
        ? Number(subscription.totalDiscount)
        : 0,
      promo_discount: subscription.promoDiscount
        ? Number(subscription.promoDiscount)
        : 0,
      referral_discount: subscription.referralDiscount
        ? Number(subscription.referralDiscount)
        : 0,
      referrer_id: subscription.referrerId || null,

      // Subscription options
      auto_renew: subscription.autoRenew || false,

      // Metadata
      device_type: subscription.deviceType || null,
      os_platform: subscription.osPlatform || null,
      app_version: subscription.appVersion || null,
      conversion_source: subscription.conversionSource || null,
      acquisition_channel: subscription.acquisitionChannel || null,
      previous_plan_id: subscription.previousPlanId || null,

      // Subscription events
      upgrade_date: subscription.upgradeDate || null,
      downgrade_date: subscription.downgradeDate || null,
      cancellation_date: subscription.cancellationDate || null,
      cancellation_reason: subscription.cancellationReason || null,

      // Administrative
      admin_notes: subscription.adminNotes || null,
      is_trial: subscription.isTrial || false,
      trial_end_date: subscription.trialEndDate || null,
      refunded: subscription.refunded || false,
      refund_date: subscription.refundDate || null,
      refund_amount: subscription.refundAmount || null,

      // Timestamps
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    console.log("Inserting subscription data:", JSON.stringify(insertData));

    // Use a direct query with explicit column names
    const { data, error } = await supabase
      .from("user_subscriptions")
      .insert([insertData])
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount, referrer_id,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at
      `
      )
      .single();

    if (error) {
      console.error("Insert error:", error);
      throw new Error(
        `Failed to insert subscription: ${JSON.stringify(error)}`
      );
    }

    if (!data) {
      throw new Error("No subscription data returned after insertion");
    }

    // Fetch the full subscription with joined data
    const { data: fullData, error: fetchError } = await supabase
      .from("user_subscriptions")
      .select(
        `
        id, user_id, subscription_plan_id, status, 
        subscription_start, subscription_end, 
        price, currency_code, payment_method_id, payment_reference, promotion_id, total_discount, promo_discount, referral_discount, referrer_id,
        auto_renew, device_type, os_platform, app_version, conversion_source, acquisition_channel,
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, 
          created_at, updated_at, plan_discount
        )
      `
      )
      .eq("id", data.id)
      .single();

    if (fetchError) {
      console.error("Fetch error:", fetchError);
      // Return the basic data even if the joined data fetch fails
      return transformUserSubscription(data);
    }

    return transformUserSubscription(fullData);
  } catch (error) {
    console.error("Error creating user subscription:", error);
    throw error;
  }
};

// Update an existing user subscription (for admin use)
export const updateUserSubscription = async (
  id: number,
  subscription: Partial<{
    status: SubscriptionStatus;
    subscriptionEnd: string | null;
    autoRenew: boolean | null;
    cancellationReason: string | null;
    cancellationDate: string | null;
    adminNotes: string | null;
    refunded: boolean | null;
    refundDate: string | null;
    refundAmount: number | null;
    additionalCharge: number | null;
  }>
): Promise<UserSubscription> => {
  try {
    console.log("updateUserSubscription called with:", { id, subscription });
    const updateData = {
      ...(subscription.status !== undefined && { status: subscription.status }),
      ...(subscription.subscriptionEnd !== undefined && {
        subscription_end: subscription.subscriptionEnd,
      }),
      ...(subscription.autoRenew !== undefined && {
        auto_renew: subscription.autoRenew,
      }),
      ...(subscription.cancellationReason !== undefined && {
        cancellation_reason: subscription.cancellationReason,
      }),
      ...(subscription.cancellationDate !== undefined && {
        cancellation_date: subscription.cancellationDate,
      }),
      ...(subscription.adminNotes !== undefined && {
        admin_notes: subscription.adminNotes,
      }),
      ...(subscription.refunded !== undefined && {
        refunded: subscription.refunded,
      }),
      ...(subscription.refundDate !== undefined && {
        refund_date: subscription.refundDate,
      }),
      ...(subscription.refundAmount !== undefined && {
        refund_amount: subscription.refundAmount,
      }),
      updated_at: formatISO(new Date()),
    };

    console.log("Final updateData being sent to database:", updateData);

    const { data, error } = await supabase
      .from("user_subscriptions")
      .update(updateData)
      .eq("id", id)
      .select(
        `
        id, user_id, subscription_plan_id, status, subscription_start, 
        subscription_end, price, currency_code, auto_renew, payment_method_id, 
        payment_reference, promotion_id, total_discount, promo_discount, referral_discount, device_type, 
        os_platform, app_version, conversion_source, acquisition_channel, 
        previous_plan_id, upgrade_date, downgrade_date, cancellation_date, cancellation_reason,
        admin_notes, is_trial, trial_end_date, refunded, refund_date, refund_amount,
        created_at, updated_at,
        subscription_plan:subscription_plans!user_subscriptions_subscription_plan_id_fkey(
          id, name, description, duration, duration_type, is_active, created_at, updated_at, plan_discount
        ),
        payment_method:payment_methods(
          id, name, description, platform_fee, fee_type, supported_currencies, is_default, is_active, created_at, updated_at
        ),
        promotion:promotions(
          id, name, description, code, discount_value, discount_type, start_date, end_date, max_uses, 
          current_uses, min_purchase_amount, applicable_plans, is_active, created_at, updated_at
        )
      `
      )
      .single();

    if (error) throw error;
    if (!data) throw new Error("No data returned after update");

    return transformUserSubscription(data);
  } catch (error) {
    console.error("Error updating user subscription:", error);
    throw error;
  }
};

// Cancel any active subscriptions for the user
const cancelActiveSubscriptions = async (userId: string) => {
  try {
    // Parse the userID to integer for database operations
    const profileId = parseInt(userId, 10);

    if (isNaN(profileId)) {
      throw new Error(`Invalid profile ID: ${userId}`);
    }

    const now = new Date().toISOString();

    // Update any active subscriptions to cancelled
    const { error } = await supabase
      .from("user_subscriptions")
      .update({
        status: "cancelled",
        cancellation_date: now,
        updated_at: now,
      })
      .eq("user_id", profileId)
      .eq("status", "active");

    if (error) {
      console.error("Error cancelling active subscriptions:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in cancelActiveSubscriptions:", error);
    throw error;
  }
};

export const fetchUserSubscriptionById = async (subscriptionId: string): Promise<UserSubscription> => {
  try {
  const { data, error } = await supabase
      .from("user_subscriptions")
      .select(`
        *,
        user:profiles!user_subscriptions_user_id_fkey (
          id,
          name,
          email
        ),
        subscriptionPlan:subscription_plans!user_subscriptions_subscription_plan_id_fkey (
          id,
          name,
          description,
          duration,
          durationType:duration_type,
          is_active,
          created_at,
          updated_at
        ),
        paymentMethod:payment_methods!user_subscriptions_payment_method_id_fkey (
          id,
          name,
          description,
          platform_fee,
          fee_type,
          supported_currencies,
          is_default,
          is_active,
          created_at,
          updated_at
        ),
        promotion:promotions!user_subscriptions_promotion_id_fkey (
          id,
          name,
          code,
          description,
          discount_value,
          discount_type,
          start_date,
          end_date,
          max_uses,
          current_uses,
          min_purchase_amount,
          applicable_plans,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('id', parseInt(subscriptionId, 10))
      .single();

    if (error) {
      console.error("Error fetching subscription:", error);
      throw new Error(`Failed to fetch subscription: ${error.message}`);
    }

    if (!data) {
      throw new Error("Subscription not found");
    }

    // Calculate discount information using correct field names - only set if > 0
    const totalPromoDiscount = data.promo_discount && data.promo_discount > 0 ? data.promo_discount : undefined;
    const totalReferralDiscount = data.referral_discount && data.referral_discount > 0 ? data.referral_discount : undefined;
    const totalDiscount = data.total_discount && data.total_discount > 0 ? data.total_discount : undefined;

    return {
      id: data.id,
      userId: data.user_id,
      subscriptionPlanId: data.subscription_plan_id,
      status: data.status as SubscriptionStatus,
      subscriptionStart: data.subscription_start,
      subscriptionEnd: data.subscription_end,
      price: data.price,
      currencyCode: data.currency_code || 'INR',
      autoRenew: data.auto_renew,
      paymentMethodId: data.payment_method_id,
      paymentReference: data.payment_reference,
      promotionId: data.promotion_id,
      isTrial: data.is_trial,
      trialEndDate: data.trial_end_date,
      cancellationDate: data.cancellation_date,
      cancellationReason: data.cancellation_reason,
      refunded: data.refunded,
      refundAmount: data.refund_amount,
      refundDate: data.refund_date,
      deviceType: data.device_type,
      osPlatform: data.os_platform,
      appVersion: data.app_version,
      conversionSource: data.conversion_source,
      acquisitionChannel: data.acquisition_channel,
      adminNotes: data.admin_notes,
      previousPlanId: data.previous_plan_id,
      upgradeDate: data.upgrade_date,
      downgradeDate: data.downgrade_date,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      // Discount calculations - only include if they have values > 0
      promoDiscount: totalPromoDiscount,
      referralDiscount: totalReferralDiscount,
      totalDiscount: totalDiscount,
      // Related data
      user: data.user,
      subscriptionPlan: data.subscriptionPlan
        ? {
            id: data.subscriptionPlan.id,
            name: data.subscriptionPlan.name,
            description: data.subscriptionPlan.description,
            duration: data.subscriptionPlan.duration,
            durationType: data.subscriptionPlan.durationType as DurationType,
            productId: undefined,
            isActive: data.subscriptionPlan.is_active,
            createdAt: data.subscriptionPlan.created_at,
            updatedAt: data.subscriptionPlan.updated_at,
            planDiscountPercentage: undefined,
            priceConfigurations: undefined,
          }
        : undefined,
      paymentMethod: data.paymentMethod
        ? {
            id: data.paymentMethod.id,
            name: data.paymentMethod.name,
            description: data.paymentMethod.description,
            platformFee: data.paymentMethod.platform_fee,
            feeType: data.paymentMethod.fee_type as FeeType,
            supportedCurrencies: data.paymentMethod.supported_currencies,
            isDefault: data.paymentMethod.is_default,
            isActive: data.paymentMethod.is_active,
            createdAt: data.paymentMethod.created_at,
            updatedAt: data.paymentMethod.updated_at,
          }
        : undefined,
      promotion: data.promotion
        ? {
            id: data.promotion.id,
            name: data.promotion.name,
            description: data.promotion.description,
            code: data.promotion.code,
            discountValue: data.promotion.discount_value,
            discountType: data.promotion.discount_type as DiscountType,
            startDate: data.promotion.start_date,
            endDate: data.promotion.end_date,
            maxUses: data.promotion.max_uses || null,
            currentUses: data.promotion.current_uses,
            minPurchaseAmount: data.promotion.min_purchase_amount || null,
            applicablePlans: data.promotion.applicable_plans || [],
            isActive: data.promotion.is_active,
            createdAt: data.promotion.created_at,
            updatedAt: data.promotion.updated_at,
          }
        : undefined,
    };
  } catch (error) {
    console.error("Error in fetchUserSubscriptionById:", error);
    throw error;
  }
};
